/**
 * Pure helpers for the inline table builder.
 *
 * The table builder dialog edits a `TableModel` — a structured form of a
 * GFM table — and the source-of-truth markdown is regenerated from it on
 * insert. When the author re-opens an existing table in the dialog,
 * `parseTableAtCursor` walks the markdown source to find the table the
 * cursor sits inside, parses it back into a `TableModel`, and reports
 * the [start, end] character offsets so the editor can splice the
 * regenerated markdown in place.
 *
 * Cells are stored as raw markdown text with `\n` for in-cell line
 * breaks. On serialization, `\n` is encoded as `<br>` so the GFM
 * source stays on a single line per cell (GFM tables don't allow
 * literal newlines in cells). The `remarkLineBreak` plugin converts
 * `<br>` back to real line breaks at render time.
 *
 * Whole-table options (`center`, `primaryCols`) are encoded as a
 * `{table:center,primary=0|2}` directive line emitted immediately
 * before the table in source. `remarkTableDirectives` consumes the
 * line and tags the following table node with corresponding CSS
 * classes — see `ArticleBody.tsx` for the style rules.
 */

import {
  formatTableDirective,
  parseTableDirective,
} from "../remarkTableDirectives";

export type TableAlignment = "left" | "center" | "right";

export interface TableModel {
  /** Header row text, one entry per column. */
  headers: string[];
  /** Body rows. Each row's length equals `headers.length` (the model
   *  is normalized on every mutation). */
  rows: string[][];
  /** Per-column alignment; length matches headers. */
  alignments: TableAlignment[];
  /** Whole-table option: render centered within the article body. */
  center?: boolean;
  /** Whole-table option: column indices to style with header-row
   *  chrome (typically `[0]` when the leftmost column holds row labels
   *  rather than data). */
  primaryCols?: number[];
}

export interface ParsedTableLocation {
  model: TableModel;
  /** Character offset in source where the table block starts (inclusive). */
  start: number;
  /** Character offset where the table block ends (exclusive). */
  end: number;
}

const MIN_COL_WIDTH = 3;

export function emptyTable(rows: number, cols: number): TableModel {
  const safeRows = Math.max(1, rows);
  const safeCols = Math.max(1, cols);
  return {
    headers: Array(safeCols).fill(""),
    rows: Array.from({ length: safeRows }, () => Array(safeCols).fill("")),
    alignments: Array(safeCols).fill("left"),
  };
}

// ---- Serialize ----------------------------------------------------------

/**
 * Render the model as well-formatted GFM markdown. Column widths are
 * sized to the widest cell so the source stays readable to anyone
 * looking at the textarea — the markdown convention is that whitespace
 * inside cells is trimmed at render time, so the padding here doesn't
 * affect the rendered output.
 *
 * In-cell newlines are encoded as `<br>` so each cell stays on a
 * single source line (GFM tables don't tolerate literal newlines).
 * Width computation uses the encoded values so the source columns
 * align after encoding.
 */
export function tableModelToMarkdown(model: TableModel): string {
  // Encode in-cell line breaks for GFM compatibility.
  const encHeaders = model.headers.map(encodeCellForSource);
  const encRows = model.rows.map((r) => r.map(encodeCellForSource));
  const widthSource: TableModel = {
    ...model,
    headers: encHeaders,
    rows: encRows,
  };
  const widths = computeColWidths(widthSource);
  const sepRow =
    "| " +
    widths
      .map((w, i) => makeSeparatorCell(model.alignments[i], w))
      .join(" | ") +
    " |";
  const tableLines = [
    formatRow(encHeaders, widths, model.alignments),
    sepRow,
    ...encRows.map((r) => formatRow(r, widths, model.alignments)),
  ];

  // Prepend a directive line only when any whole-table option is set.
  // Authors hand-editing the markdown can also write the directive
  // themselves; `parseTableAtCursor` reads it back into the model.
  const directive = formatTableDirective({
    center: model.center,
    primaryCols: model.primaryCols,
  });
  return directive ? directive + "\n" + tableLines.join("\n") : tableLines.join("\n");
}

function encodeCellForSource(text: string): string {
  // \n → <br> for in-cell line breaks. Order matters if other
  // encodings are added later (e.g. piped characters), but for now
  // this is the only transform.
  return text.replace(/\n/g, "<br>");
}

function decodeCellFromSource(text: string): string {
  return text.replace(/<br\s*\/?>/gi, "\n");
}

function formatRow(
  cells: string[],
  widths: number[],
  alignments: TableAlignment[],
): string {
  return (
    "| " +
    widths
      .map((w, i) => padCell(cells[i] ?? "", w, alignments[i]))
      .join(" | ") +
    " |"
  );
}

function computeColWidths(model: TableModel): number[] {
  return model.headers.map((_, col) => {
    let max = MIN_COL_WIDTH;
    if ((model.headers[col] ?? "").length > max) max = model.headers[col].length;
    for (const row of model.rows) {
      const cell = row[col] ?? "";
      if (cell.length > max) max = cell.length;
    }
    return max;
  });
}

function padCell(text: string, width: number, alignment: TableAlignment): string {
  const t = text ?? "";
  if (t.length >= width) return t;
  const pad = width - t.length;
  if (alignment === "right") return " ".repeat(pad) + t;
  if (alignment === "center") {
    const left = Math.floor(pad / 2);
    return " ".repeat(left) + t + " ".repeat(pad - left);
  }
  return t + " ".repeat(pad);
}

function makeSeparatorCell(alignment: TableAlignment, width: number): string {
  // Min visual width 3 so `---` is always present.
  const w = Math.max(MIN_COL_WIDTH, width);
  if (alignment === "center") {
    return ":" + "-".repeat(w - 2) + ":";
  }
  if (alignment === "right") {
    return "-".repeat(w - 1) + ":";
  }
  return "-".repeat(w);
}

// ---- Mutators (return new models) --------------------------------------

export function setHeader(model: TableModel, col: number, value: string): TableModel {
  return {
    ...model,
    headers: model.headers.map((h, i) => (i === col ? value : h)),
  };
}

export function setCell(
  model: TableModel,
  row: number,
  col: number,
  value: string,
): TableModel {
  return {
    ...model,
    rows: model.rows.map((r, i) =>
      i === row ? r.map((c, j) => (j === col ? value : c)) : r,
    ),
  };
}

export function setAlignment(
  model: TableModel,
  col: number,
  alignment: TableAlignment,
): TableModel {
  return {
    ...model,
    alignments: model.alignments.map((a, i) => (i === col ? alignment : a)),
  };
}

export function addColumn(model: TableModel, atIndex?: number): TableModel {
  const idx = atIndex ?? model.headers.length;
  // Shift any primary-column indices at or above `idx` so the
  // primary flag stays attached to the same column visually.
  const primaryCols = model.primaryCols?.map((c) => (c >= idx ? c + 1 : c));
  return {
    ...model,
    headers: insertAt(model.headers, idx, ""),
    rows: model.rows.map((r) => insertAt(r, idx, "")),
    alignments: insertAt(model.alignments, idx, "left" as TableAlignment),
    primaryCols,
  };
}

export function removeColumn(model: TableModel, col: number): TableModel {
  if (model.headers.length <= 1) return model; // keep at least one column
  // Drop the removed column from primaryCols (if present) and shift
  // any higher indices down by one to track the renumbering.
  const primaryCols = model.primaryCols
    ?.filter((c) => c !== col)
    .map((c) => (c > col ? c - 1 : c));
  return {
    ...model,
    headers: removeAt(model.headers, col),
    rows: model.rows.map((r) => removeAt(r, col)),
    alignments: removeAt(model.alignments, col),
    primaryCols: primaryCols && primaryCols.length > 0 ? primaryCols : undefined,
  };
}

export function togglePrimaryCol(model: TableModel, col: number): TableModel {
  const current = model.primaryCols ?? [];
  const isPrimary = current.includes(col);
  const next = isPrimary
    ? current.filter((c) => c !== col)
    : [...current, col].sort((a, b) => a - b);
  return { ...model, primaryCols: next.length > 0 ? next : undefined };
}

export function setCenter(model: TableModel, center: boolean): TableModel {
  return { ...model, center: center ? true : undefined };
}

export function addRow(model: TableModel, atIndex?: number): TableModel {
  const idx = atIndex ?? model.rows.length;
  const empty = Array(model.headers.length).fill("");
  return { ...model, rows: insertAt(model.rows, idx, empty) };
}

export function removeRow(model: TableModel, row: number): TableModel {
  if (model.rows.length <= 1) return model; // keep at least one row
  return { ...model, rows: removeAt(model.rows, row) };
}

function insertAt<T>(arr: T[], idx: number, value: T): T[] {
  const out = arr.slice();
  out.splice(idx, 0, value);
  return out;
}

function removeAt<T>(arr: T[], idx: number): T[] {
  const out = arr.slice();
  out.splice(idx, 1);
  return out;
}

// ---- Parse markdown at cursor ------------------------------------------

/**
 * Detect whether the cursor sits inside a GFM table block. If so,
 * parse the block back into a `TableModel` and report the [start, end]
 * character offsets in the source so the editor can replace the block
 * verbatim on insert.
 *
 * GFM table structure we look for:
 *   - Header row:  `| col | col | col |`
 *   - Separator:   `| --- | :-: | --: |`  (the `-` is required; colons
 *                   are optional alignment markers)
 *   - Body rows:   `| val | val | val |`
 *
 * We anchor on the separator row (it's the most distinctive line). From
 * there we walk up one line for the header and down for body rows until
 * we hit a non-table line.
 */
export function parseTableAtCursor(
  source: string,
  cursorOffset: number,
): ParsedTableLocation | null {
  const lines = source.split("\n");
  const cursorLine = lineIndexAtOffset(lines, cursorOffset);

  const isSep = (line: string): boolean =>
    /^\s*\|.*\|\s*$/.test(line) &&
    /-/.test(line) &&
    /^[\s|:-]+$/.test(line);
  const isRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line);

  // Walk up from cursor to find the separator. If the cursor is on the
  // separator itself, accept it directly. If above the separator (on the
  // header line), walk down once. Otherwise walk up.
  let sepLine = -1;
  if (isSep(lines[cursorLine] ?? "")) {
    sepLine = cursorLine;
  } else {
    for (let i = cursorLine; i >= 0; i--) {
      if (!isRow(lines[i] ?? "")) break;
      if (isSep(lines[i] ?? "")) {
        sepLine = i;
        break;
      }
    }
    if (sepLine < 0) {
      for (let i = cursorLine + 1; i < lines.length; i++) {
        if (!isRow(lines[i] ?? "")) break;
        if (isSep(lines[i] ?? "")) {
          sepLine = i;
          break;
        }
      }
    }
  }
  if (sepLine < 0) return null;

  const headerLine = sepLine - 1;
  if (headerLine < 0 || !isRow(lines[headerLine] ?? "")) return null;

  let endLine = sepLine;
  for (let i = sepLine + 1; i < lines.length; i++) {
    const ln = lines[i] ?? "";
    if (!isRow(ln) || !ln.trim()) break;
    endLine = i;
  }

  // Look one line above the header for a `{table:...}` directive.
  // If present, the captured range expands to include it so the
  // editor's "replace" splice cleanly swaps the whole block when the
  // dialog regenerates the markdown.
  let directiveLine = -1;
  let directive: ReturnType<typeof parseTableDirective> = null;
  if (headerLine >= 1) {
    const above = lines[headerLine - 1] ?? "";
    const parsed = parseTableDirective(above);
    if (parsed) {
      directiveLine = headerLine - 1;
      directive = parsed;
    }
  }

  // Character offsets.
  const start = offsetForLineStart(
    lines,
    directiveLine >= 0 ? directiveLine : headerLine,
  );
  const end = offsetForLineStart(lines, endLine) + (lines[endLine]?.length ?? 0);

  // Parse cells; decode `<br>` back to `\n` for the dialog's
  // multiline TextFields so authors see their line breaks visually.
  const headers = splitCells(lines[headerLine] ?? "").map(decodeCellFromSource);
  const sepCells = splitCells(lines[sepLine] ?? "");
  const alignments: TableAlignment[] = sepCells.map((c) => {
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });
  const bodyRows = lines
    .slice(sepLine + 1, endLine + 1)
    .map((l) => splitCells(l).map(decodeCellFromSource));

  // Normalize column count — pad short rows, trim long ones.
  const cols = headers.length;
  while (alignments.length < cols) alignments.push("left");
  const rows = bodyRows.map((r) => {
    const out = r.slice(0, cols);
    while (out.length < cols) out.push("");
    return out;
  });

  return {
    model: {
      headers,
      rows,
      alignments: alignments.slice(0, cols),
      center: directive?.center,
      primaryCols: directive?.primaryCols,
    },
    start,
    end,
  };
}

function splitCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function lineIndexAtOffset(lines: string[], offset: number): number {
  let pos = 0;
  for (let i = 0; i < lines.length; i++) {
    const lineLen = lines[i].length + 1; // +1 for the \n we split on
    if (offset < pos + lineLen) return i;
    pos += lineLen;
  }
  return lines.length - 1;
}

function offsetForLineStart(lines: string[], line: number): number {
  let pos = 0;
  for (let i = 0; i < line; i++) pos += lines[i].length + 1;
  return pos;
}
